import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button, Container } from '@mui/material';
import type { ReactNode } from 'react';
import { Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { EmptyState, LoadingState } from '@/components';
import type { AppRole } from '@/types/domain';
import { useAuth } from './AuthProvider';
import { safeNext } from './authService';

/** Requires a signed-in user. Redirects to /login and returns afterwards. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { initialised, user } = useAuth();
  const location = useLocation();
  if (!initialised) return <LoadingState />;
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Hides UI the user cannot use. This is a convenience only: the database and
 * Edge Functions enforce the same rules for every request.
 */
export function RequireRole({ role, children, fallback }: { role: AppRole; children: ReactNode; fallback?: ReactNode }) {
  const { initialised, user, hasRole } = useAuth();
  const location = useLocation();
  if (!initialised) return <LoadingState />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (!hasRole(role)) {
    if (fallback) return <>{fallback}</>;
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <EmptyState
          icon={<LockOutlinedIcon />}
          title="You don't have access to this area"
          description={role === 'admin' ? 'This area is for TradePrep administrators.' : 'This area is for approved TradePrep content providers.'}
          action={
            <Button variant="contained" component={RouterLink} to="/dashboard">
              Go to dashboard
            </Button>
          }
        />
      </Container>
    );
  }
  return <>{children}</>;
}

export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { initialised, user, recoveringPassword } = useAuth();
  const location = useLocation();
  if (!initialised) return <LoadingState />;
  if (user && !recoveringPassword) {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={safeNext(next) ?? '/dashboard'} replace />;
  }
  return <>{children}</>;
}
